import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { SearchItem } from "@/components/ui/search"
import { joinStrings } from "@/lib/joinString"
import { CareProviderIcon } from "@/modules/care-provider/components/care-provider-icon"
import { careProviderSearchDoctorNetworksQuery$data } from "@/modules/care-provider/simple-search/__generated__/careProviderSearchDoctorNetworksQuery.graphql"
import { type CareProviderOption } from "@/modules/care-provider/simple-search/care-provider-search"
import { type ApplicableInsuranceCareProviderListIds } from "@/modules/care-provider/simple-search/services/useCareProviderQuickListQuery"
import { combineConditionalCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { getCareProviderAddress } from "@/modules/care-provider/utils/get-care-provider-address"
import { getCareProviderIndicator } from "@/modules/care-provider/utils/get-care-provider-indicator"
import { getCareProviderRelationName } from "@/modules/care-provider/utils/get-care-provider-relation-name"
import { getScheduleSummary } from "@/modules/care-provider/utils/get-schedule-summary"

type CareProviderItemProps = {
  doctorNetworks?: NonNullable<careProviderSearchDoctorNetworksQuery$data["ch_patientById"]>["doctorNetworks"]
  hasMPNReferral?: boolean
  applicableInsuranceCareProviderListIds?: ApplicableInsuranceCareProviderListIds
  option: CareProviderOption
  onSelect: (value: string) => void
}

const CareProviderItem = ({
  option,
  doctorNetworks,
  hasMPNReferral,
  applicableInsuranceCareProviderListIds,
  onSelect,
}: CareProviderItemProps) => {
  const { t } = useTranslation()
  const highlights = "highlights" in option ? option.highlights : null
  const title = combineConditionalCareProviderTitle(option, highlights)
  const address = getCareProviderAddress(option)

  const doctorNetwork = useMemo(() => {
    return doctorNetworks?.find((x) => x.careProvider?.id === option.id)
  }, [doctorNetworks, option.id])

  const firstSpecialty = option.careProviderSpecialtyTranslations?.[0]?.name
  const relation = getCareProviderRelationName(option, t)
  const scheduleSummary =
    option.detailedSchedule && option.detailedSchedule.length > 0 ? getScheduleSummary(option.detailedSchedule, t) : ""

  return (
    <SearchItem
      key={option.id}
      value={option.id}
      onSelect={() => onSelect(option.id)}
      data-testid="care-provider-search-item">
      <div className="flex w-full items-center gap-4">
        <CareProviderIcon groupType={option.careProviderGroupQuickList.groupType} />
        <div className="flex-1 overflow-hidden">
          <PotentiallyUnsafeInnerHtml className="truncate" value={title} data-testid="care-provider-item-name" />
          <PotentiallyUnsafeInnerHtml className="text-primary truncate text-xs" value={relation} />
          <PotentiallyUnsafeInnerHtml
            className="text-on-surface-subdued truncate text-xs"
            value={joinStrings([firstSpecialty, option.groupName], ", ")}
          />
          <PotentiallyUnsafeInnerHtml className="text-on-surface-subdued truncate text-xs" value={address} />
          {scheduleSummary && <p className="text-on-surface-subdued truncate text-xs">{scheduleSummary}</p>}
        </div>
        <div className="inline-flex w-min shrink-0 items-center has-[span]:px-2 [&>span]:w-auto">
          {getCareProviderIndicator({
            isMedgatePartnerNetworkMember: option.isMedgatePartnerNetworkMember,
            isTakesNewPatients: option.isTakesNewPatients,
            role: doctorNetwork?.role,
            defaultMedgatePartnerNetworkAllowed: hasMPNReferral,
            insuranceCareProviderListIds: option.insuranceCareProviderListIds,
            applicableInsuranceCareProviderListIds,
          })}
        </div>
      </div>
    </SearchItem>
  )
}

export { CareProviderItem }
